import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FilterField } from '@/components/list/filters';
import { useListParams } from '@/components/list/use-list-params';
import { monthLabel, monthRange, recentMonths } from '@/expenses/list/list-query';
import { listLog } from '../api';
import type { LogFilters } from '../api-types';
import { useLabels } from '../labels';
import { shortId } from '../format';
import { type LogRow, mergeRows, rowsOf } from './rows';

const FILTER_KEYS = ['user', 'step', 'profile', 'period'] as const;

export type LogState =
  | { status: 'loading' }
  | { status: 'error'; httpStatus: number }
  | {
      status: 'ready';
      items: LogRow[];
      nextCursor: string | null;
      loadingMore: boolean;
      moreFailed: boolean;
    };

type Known = { users: Map<string, string>; steps: Set<string>; profiles: Set<string> };

/** What the filters offer: the values met so far, kept even when a filter narrows the list. */
function useKnown(state: LogState): Known {
  const [known, setKnown] = useState<Known>(() => ({
    users: new Map(),
    steps: new Set(),
    profiles: new Set(),
  }));
  useEffect(() => {
    if (state.status !== 'ready') return;
    setKnown((previous) => {
      const next: Known = {
        users: new Map(previous.users),
        steps: new Set(previous.steps),
        profiles: new Set(previous.profiles),
      };
      for (const item of state.items) {
        next.users.set(item.userId, shortId(item.userId));
        if (item.step !== null) next.steps.add(item.step);
        if (item.profile !== null) next.profiles.add(item.profile);
      }
      return next;
    });
  }, [state]);
  return known;
}

/** The log's pages for the filters in force: new filters start again from the first page. */
function useLogPages(filters: LogFilters) {
  const [state, setState] = useState<LogState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const generation = useRef(0);
  const key = JSON.stringify(filters);

  useEffect(() => {
    const mine = ++generation.current;
    setState({ status: 'loading' });
    void listLog(JSON.parse(key) as LogFilters).then((answer) => {
      if (mine !== generation.current) return;
      setState(
        answer.ok
          ? {
              status: 'ready',
              items: mergeRows([], rowsOf(answer.value)),
              nextCursor: answer.value.nextCursor,
              loadingMore: false,
              moreFailed: false,
            }
          : { status: 'error', httpStatus: answer.status },
      );
    });
  }, [key, attempt]);

  const loadMore = useCallback(() => {
    if (state.status !== 'ready' || state.nextCursor === null || state.loadingMore) return;
    const mine = generation.current;
    const cursor = state.nextCursor;
    setState({ ...state, loadingMore: true, moreFailed: false });
    void listLog({ ...(JSON.parse(key) as LogFilters), cursor }).then((answer) => {
      if (mine !== generation.current) return;
      setState((current) => {
        if (current.status !== 'ready') return current;
        if (!answer.ok) return { ...current, loadingMore: false, moreFailed: true };
        return {
          ...current,
          items: mergeRows(current.items, rowsOf(answer.value)),
          nextCursor: answer.value.nextCursor,
          loadingMore: false,
        };
      });
    });
  }, [state, key]);

  return { state, loadMore, reload: () => setAttempt((n) => n + 1) };
}

/** The filters the toolbar offers, from what the log has shown so far. */
function useLogFields(state: LogState): FilterField[] {
  const { t, i18n } = useTranslation('communications');
  const known = useKnown(state);
  const labels = useLabels();
  return useMemo<FilterField[]>(
    () => [
      {
        key: 'user',
        label: t('log.filters.user'),
        options: [...known.users].map(([value, label]) => ({ value, label })),
      },
      {
        key: 'step',
        label: t('log.filters.step'),
        options: [...known.steps].map((value) => ({ value, label: labels.step(value) })),
      },
      {
        key: 'profile',
        label: t('log.filters.profile'),
        options: [...known.profiles].map((value) => ({ value, label: labels.profile(value) })),
      },
      {
        key: 'period',
        label: t('log.filters.period'),
        options: recentMonths(new Date()).map((m) => ({
          value: m,
          label: monthLabel(m, i18n.resolvedLanguage),
        })),
      },
    ],
    [t, i18n.resolvedLanguage, known, labels],
  );
}

/** The log's filters (kept in the URL), its pages, and the filters as the API and the CSV read them. */
export function useLog() {
  const { active, q, set, clear } = useListParams(FILTER_KEYS);

  const filters = useMemo<LogFilters>(() => {
    const out: LogFilters = {};
    if (active['user']) out.userId = active['user'];
    if (active['step']) out.step = active['step'];
    if (active['profile']) out.profile = active['profile'];
    const range = active['period'] ? monthRange(active['period']) : null;
    if (range !== null) Object.assign(out, range);
    return out;
  }, [active]);
  const { state, loadMore, reload } = useLogPages(filters);
  const fields = useLogFields(state);

  // The search narrows the loaded page by name or detail: the API has no text filter.
  const shown = useMemo(() => {
    if (state.status !== 'ready') return [];
    const needle = q.trim().toLowerCase();
    return needle === ''
      ? state.items
      : state.items.filter((i) => `${i.userId} ${i.label}`.toLowerCase().includes(needle));
  }, [state, q]);

  return {
    state,
    shown,
    fields,
    filters,
    active,
    q,
    set,
    clear,
    loadMore,
    reload,
    narrowed: q !== '' || Object.keys(filters).length > 0,
  };
}
